import type {
  ApiTimestamp,
  ObservedObject,
  OrphanDisposition,
} from '@/features/delivery/types/contracts';
import { z } from 'zod';

const nonBlankText = z.string().refine((value) => value.trim().length > 0, {
  message: 'must_not_be_blank',
});
const count = z.number().int().nonnegative().safe();

export type OutputBuildState = 'pending' | 'blocked' | 'building' | 'failed' | 'ready';
export type OutputKind =
  | 'egocentric_mcap'
  | 'orange_egocentric_mcap'
  | 'hed_hdf5'
  | 'umi_hdf5'
  | 'mp4';
export type OutputResourceKind =
  | 'delivery_mcap'
  | 'delivery_sidecar'
  | 'delivery_hdf5'
  | 'delivery_mp4';
export type OutputEntryReasonCode =
  | 'missing_inputs'
  | 'invalid_input'
  | 'source_voided'
  | 'task_failed'
  | 'task_cancelled'
  | 'invalid_output';

export type MetadataStatus = 'ok' | 'warning' | 'incomplete' | 'not_evaluated';
export type BuiltMetadataSnapshot = Record<string, string | number>;
export type MetadataSnapshot = Record<string, unknown>;
interface MetadataSnapshotEntry {
  customerKey: string;
  value: unknown;
}
export type MetadataSnapshotResponse = MetadataSnapshot | readonly MetadataSnapshotEntry[];

export interface RobocapCalibrationBundle {
  bundleObject: string;
  bundleGeneration: string;
  bundleBlake3: string;
  bundleSizeBytes: number;
}
export interface UmiHandposeCalibration {
  closedMillimeters: number;
  openMillimeters: number;
}
export interface OutputBuildOptionsV1 {
  schemaVersion: 1;
  vio: null;
}

export interface RobocapCalibrationBuildOptions {
  schemaVersion: 2;
  vio: null;
  robocapCalibration: {
    mode: 'missing_tf_only';
    bundleObject: string;
    bundleGeneration: string;
    bundleBlake3: string;
    bundleSizeBytes: number;
  };
}

export interface UmiHandposeBuildOptions {
  schemaVersion: 2;
  vio: null;
  umiHandposeCalibration: { closedMillimeters: number; openMillimeters: number };
}

export interface HedBuildOptions {
  schemaVersion: 2;
  vio: null;
  hedWindowPolicy: 'full_episode';
}

/** A strict wire union: schema v2 admits exactly one kind-specific field. */
export type OutputBuildOptions =
  | OutputBuildOptionsV1
  | RobocapCalibrationBuildOptions
  | UmiHandposeBuildOptions
  | HedBuildOptions;

export interface OutputResourceConfig {
  schemaVersion: 1;
  resources: OutputResourceKind[];
}

export interface DeliveryOutputConfig {
  deliveryId: string;
  outputKind: OutputKind;
  resourceConfig: OutputResourceConfig;
  outputOptions: OutputBuildOptions;
  createdBy: string;
  finalizationState: 'open' | 'finalizing' | 'finalized';
  finalizationGeneration: number;
  finalizationTaskId: string | null;
  finalPublication: {
    manifest: DeliveryPublishedArtifact;
    checksums: DeliveryPublishedArtifact;
    producerTaskId: string;
    generation: number;
  } | null;
  createdAt?: ApiTimestamp;
  updatedAt?: ApiTimestamp;
}

export interface DeliveryPublishedArtifact {
  object: string;
  gcsGeneration: string;
  contentHash: string;
  sizeBytes: number;
}

interface OutputDuration {
  totalMs: number;
  pendingMs: number;
  blockedMs: number;
  buildingMs: number;
  readyMs: number;
  failedMs: number;
}

export interface OutputSummary {
  deliveryId: string;
  state: OutputBuildState;
  entries: {
    total: number;
    pending: number;
    blocked: number;
    building: number;
    ready: number;
    failed: number;
  };
  duration: OutputDuration;
  metadata: {
    total: number;
    ok: number;
    warning: number;
    incomplete: number;
    notEvaluated: number;
  };
}

interface OutputEntryReason {
  code: OutputEntryReasonCode;
  details: {
    missingResources: OutputResourceKind[];
    message: string | null;
  };
}

interface OutputEntryOutput {
  outputKind: OutputKind;
  directoryUri: string;
  primaryUri: string;
  gcsGeneration: string;
  contentHash: string;
  sizeBytes: number;
  producerTaskId: string;
  builtMetadataSnapshot: BuiltMetadataSnapshot | null;
  sidecar: { object: string; gcsGeneration: string; contentHash: string; sizeBytes: number } | null;
  robocapCalibrationProvenance:
    | (RobocapCalibrationBundle & {
        fallbackApplied: boolean;
        serial: string | null;
        yaml: { path: string; sha256: string }[];
      })
    | null;
  createdAt?: ApiTimestamp;
}

export interface OutputEntryView {
  deliveryId: string;
  entryId: string;
  entryKey?: string;
  segmentId?: string;
  segmentKey?: string;
  assetId?: string;
  assetKey?: string;
  filename?: string;
  durationMs?: number;
  batchNo: number;
  ordinal: number;
  state: OutputBuildState;
  buildGeneration: number;
  currentTaskId: string | null;
  reason: OutputEntryReason | null;
  lastRetryReason: string | null;
  sourceState: 'active' | 'voided';
  finalOrdinal: number | null;
  output: OutputEntryOutput | null;
  createdAt?: ApiTimestamp;
  updatedAt?: ApiTimestamp;
  builtMetadataSnapshot: BuiltMetadataSnapshot | null;
  metadataWarnings?: MetadataWarning[];
  metadataMissingRequired?: string[];
  metadataStatus?: MetadataStatus;
  metadataMessage?: string;
  metadataSnapshot?: MetadataSnapshot | null;
}

export type OutputEntryViewResponse = Omit<OutputEntryView, 'metadataSnapshot'> & {
  metadataSnapshot?: MetadataSnapshotResponse | null;
};

export interface OutputEntryPage {
  items: OutputEntryView[];
  nextCursor?: string | null;
}

export interface OutputEntryPageResponse extends Omit<OutputEntryPage, 'items'> {
  items: OutputEntryViewResponse[];
}

export interface OutputBuildResult {
  total: number;
  queued: number;
  blocked: number;
  unchanged: number;
}

type OutputManifestStatus = 'previewed' | 'published' | 'refused' | 'unavailable';

interface OutputManifestBatchResult {
  batchNo: number;
  status: OutputManifestStatus;
  object: string | null;
  fileCount: number | null;
  totalSizeBytes: number | null;
  sizeBytes: number | null;
  generation: string | null;
  detail: string | null;
}

export interface OutputManifestReport {
  deliveryId: string;
  batches: OutputManifestBatchResult[];
  complete: boolean;
}

export type OutputManifestMode = 'preview' | 'apply';

export interface OutputEntryBuildResult {
  entryId: string;
  state: OutputBuildState;
  buildGeneration: number;
  currentTaskId: string | null;
  reason: OutputEntryReason | null;
  queued: boolean;
  createdAt?: ApiTimestamp;
  updatedAt?: ApiTimestamp;
}

export interface OutputReadLease {
  url: string;
  expiresAt: string;
  name: string;
  mediaType: string;
  sizeBytes: number;
  contentHash: string;
  gcsGeneration: string;
}

export interface CustomerOutputOrphan {
  entryId: string;
  deliveryId: string;
  batchNo: number;
  ordinal: number;
  object: string;
  receiptGeneration: string;
  observed: ObservedObject;
  disposition: OrphanDisposition;
}

export interface CustomerOutputOrphanPage {
  items: CustomerOutputOrphan[];
  complete: boolean;
  nextCursor?: string | null;
}

export type PurgeOutcome =
  | 'purged'
  | 'unconfirmed'
  | 'not_purgeable'
  | 'generation_fence_mismatch'
  | 'no_receipt'
  | 'not_found';

export interface PurgeOrphanResultItem {
  entryId: string;
  outcome: PurgeOutcome;
  generation: string | null;
}

export interface PurgeOrphansResult {
  items: PurgeOrphanResultItem[];
}

export interface OutputEntryMetadataResult {
  entryId?: string;
  builtMetadataSnapshot: BuiltMetadataSnapshot | null;
  metadataWarnings?: MetadataWarning[];
  metadataMissingRequired?: string[];
  metadataStatus?: MetadataStatus;
  metadataMessage?: string;
  metadataSnapshot?: MetadataSnapshot | null;
}

export type OutputEntryMetadataResultResponse = Omit<
  OutputEntryMetadataResult,
  'metadataSnapshot'
> & {
  metadataSnapshot?: MetadataSnapshotResponse | null;
};

export interface ResolveOutputMetadataResult {
  resolved?: number;
}

const outputBuildOptionsV1InputSchema = z
  .object({
    schemaVersion: z.literal(1),
    vio: z.null(),
  })
  .strict();

const robocapCalibrationInputSchema = z
  .object({
    mode: z.literal('missing_tf_only'),
    bundleObject: z.string().min(1),
    bundleGeneration: z.string().min(1),
    bundleBlake3: z.string().min(1),
    bundleSizeBytes: z.number().int().nonnegative().safe(),
  })
  .strict();

const robocapBuildOptionsInputSchema = z
  .object({
    schemaVersion: z.literal(2),
    vio: z.null(),
    robocapCalibration: robocapCalibrationInputSchema,
  })
  .strict();

const umiHandposeBuildOptionsInputSchema = z
  .object({
    schemaVersion: z.literal(2),
    vio: z.null(),
    umiHandposeCalibration: z
      .object({
        closedMillimeters: count,
        openMillimeters: count.max(1000),
      })
      .strict(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      value.umiHandposeCalibration.closedMillimeters >= value.umiHandposeCalibration.openMillimeters
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['umiHandposeCalibration', 'closedMillimeters'],
        message: 'umi_closed_must_be_less_than_open',
      });
    }
  });

const hedBuildOptionsInputSchema = z
  .object({
    schemaVersion: z.literal(2),
    vio: z.null(),
    hedWindowPolicy: z.literal('full_episode'),
  })
  .strict();

export const outputBuildOptionsInputSchema = z.union([
  outputBuildOptionsV1InputSchema,
  robocapBuildOptionsInputSchema,
  umiHandposeBuildOptionsInputSchema,
  hedBuildOptionsInputSchema,
]);

export const outputKindSchema = z.enum([
  'egocentric_mcap',
  'orange_egocentric_mcap',
  'hed_hdf5',
  'umi_hdf5',
  'mp4',
]);
export function validateOutputOptions(
  input: { outputKind: OutputKind; outputOptions: OutputBuildOptions },
  context: z.RefinementCtx,
): void {
  const options = input.outputOptions;
  if (options.schemaVersion === 1) return;
  const compatible =
    'robocapCalibration' in options
      ? input.outputKind === 'egocentric_mcap' || input.outputKind === 'orange_egocentric_mcap'
      : 'umiHandposeCalibration' in options
        ? input.outputKind === 'umi_hdf5'
        : input.outputKind === 'hed_hdf5';
  if (!compatible)
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['outputOptions'],
      message: 'incompatible_output_kind_options',
    });
}

export const purgeOrphansInputSchema = z
  .object({
    entries: z
      .array(
        z.object({ entryId: z.string().min(1), expectedGcsGeneration: z.string().min(1) }).strict(),
      )
      .min(1),
    reason: nonBlankText,
  })
  .strict();
export type PurgeOrphansInput = z.infer<typeof purgeOrphansInputSchema>;

export const changeOutputConfigInputSchema = z
  .object({
    outputKind: outputKindSchema,
    outputOptions: outputBuildOptionsInputSchema,
  })
  .strict()
  .superRefine(validateOutputOptions);

export type ChangeOutputConfigInput = z.infer<typeof changeOutputConfigInputSchema>;

export const outputManifestModeSchema = z.enum(['preview', 'apply']);

const buildScopeSchema = z.enum(['pending', 'failed', 'terminal']);
export type BuildScope = z.infer<typeof buildScopeSchema>;

function buildScopeNeedsReason(scope: BuildScope | undefined): boolean {
  return scope === 'failed' || scope === 'terminal';
}

export const buildScopeInputSchema = z
  .object({
    scope: buildScopeSchema.optional(),
    reason: nonBlankText.optional(),
  })
  .strict()
  .superRefine((body, context) => {
    if (buildScopeNeedsReason(body.scope)) {
      if (!body.reason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reason'],
          message: 'reason_required',
        });
      }
    } else if (body.reason !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: 'reason_forbidden',
      });
    }
  });

export type BuildScopeInput = z.infer<typeof buildScopeInputSchema>;

export const rerunOutputEntryInputSchema = z
  .object({
    scope: buildScopeSchema,
    reason: nonBlankText.optional(),
    expectedBuildGeneration: count,
  })
  .strict()
  .superRefine((body, context) => {
    if (buildScopeNeedsReason(body.scope)) {
      if (!body.reason) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reason'],
          message: 'reason_required',
        });
      }
    } else if (body.reason !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reason'],
        message: 'reason_forbidden',
      });
    }
  });

export type RerunOutputEntryInput = z.infer<typeof rerunOutputEntryInputSchema>;

export const patchOutputEntryMetadataInputSchema = z.record(z.string(), z.unknown());
export type PatchOutputEntryMetadataInput = z.infer<typeof patchOutputEntryMetadataInputSchema>;

export interface DeliveryFinalizeTaskResult {
  deliveryId: string;
  taskId: string;
  state: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  reconciled: boolean;
  selectedEntries: number;
  excludedEntries: number;
}
export type DeliveryFinalizeResult = OutputManifestReport | DeliveryFinalizeTaskResult;

export type MetadataWarning =
  | { code: 'schema_nonconformant' }
  | {
      code: 'built_metadata_mismatch';
      missingInputKeys: string[];
      changedInputKeys: string[];
    };
